console.log('🚀 TemplateSelector.tsx file is being loaded!');

import { createScaffolderFieldExtension, FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { useEffect, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import type { FieldValidation } from '@rjsf/utils';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api'; // ⬅️ ¡Importaciones clave!

console.log('📦 TemplateSelector imports successful!');

interface Template {
  name: string;
  description: string;
  type: string;
  versions: string[];
  current_version: string;
}

interface TemplateSelectorValue {
  template: string;
  version: string;
  branch: string;
}

interface TemplateSelectorFieldProps extends FieldExtensionComponentProps<TemplateSelectorValue> {
  template_type?: string;
}

export const TemplateSelectorField = (props: TemplateSelectorFieldProps) => {
  console.log('🎯 TEMPLATE SELECTOR IS RENDERING!', props);
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const SYS_INFO_APP_URL = 'app.baseUrl';
  const SYS_INFO_BACKEND_URL = 'backend.baseUrl';
  const appUrl = config.getString(SYS_INFO_APP_URL);
  const backendUrl = config.getString(SYS_INFO_BACKEND_URL);

  const [environment, setEnvironment] = useState('');
  const [branch, setBranch] = useState('');
  const [branches, setBranches] = useState<string[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentValue = props.formData || { template: '', version: '', branch: '' };
  const selectedTemplate = templates.find(t => t.name === currentValue.template);


  // Fetch templates when template_type or branch changes
  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.template_type, branch]);

  // Cuando cambia la branch, recargar templates de esa branch
  useEffect(() => {
    if (!branch) return;

    // Resetear selección actual del formulario
    props.onChange({ template: '', version: '', branch: branch });

    // Volver a cargar templates
    fetchTemplates();
  }, [branch]);


  const fetchBranches = async () => {
    try {
      const token = (await identityApi.getCredentials()).token;
      version: selectedTemplate?.current_version || 'latest' // Always use latest version

      const response = await fetch(`${backendUrl}/api/devops-api-proxy/branches`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const branchesData = await response.json();
      setBranches(branchesData);

    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = (await identityApi.getCredentials()).token;

      // Determine type filter
      const typeFilter = 'ci';

      const url = `${backendUrl}/api/devops-api-proxy/templates?templateBranch=${encodeURIComponent(branch)}`;
      console.log('🔍 Fetching templates from:', url);

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const templatesData = await response.json();
      console.log('📋 Templates fetched:', templatesData);
      // Filter by type
      const filteredTemplates = (templatesData.apiPayload || []).filter((tpl: any) => tpl.type === typeFilter);
      setTemplates(filteredTemplates);
      if (!branch || branch === '') {
        // If branch is empty, set it and re-fetch templates for the new branch
        setBranch(templatesData.defaultBranch);
        return; // Exit to avoid using old branch value
      }
      setEnvironment(templatesData.environment);

      if (templatesData.environment === 'preproduction') {
        await fetchBranches();
      }

    } catch (error) {
      console.error('❌ Error fetching templates:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };


  const handleTemplateChange = (templateName: string) => {
    const template = templates.find(t => t.name === templateName);
    const newValue = {
      branch: branch,
      template: templateName,
      version: template?.current_version || ''
    };
    console.log('🔧 Template changed:', newValue);
    props.onChange(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gridGap={2} p={2}>
        <CircularProgress size={20} />
        <Typography>Loading templates...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error" style={{ marginBottom: 16 }}>
          Error loading templates: {error}
        </Alert>
        <button
          onClick={fetchTemplates}
          style={{
            padding: '8px 16px',
            // backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🔄 Retry
        </button>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Typography variant="h6" gutterBottom>
        📦 Template Configuration
      </Typography>

      <Box display="flex" gridGap={2} flexDirection="column">
        {/* Template Selector */}
        {environment === 'preproduction' && (
          <FormControl fullWidth variant="standard">
            <InputLabel>Branch</InputLabel>
            <Select
              value={currentValue.branch}
              onChange={(e) => setBranch(e.target.value as string)}
              label="Branch"
            >
              {branches.map(b => (
                <MenuItem key={b} value={b}>{b}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl fullWidth variant="standard">
          <InputLabel>Template</InputLabel>
          <Select
            value={currentValue.template}
            onChange={(e) => handleTemplateChange(e.target.value as string)}
            label="Template"
          >
            <MenuItem value="">
              <em>Select a template</em>
            </MenuItem>
            {templates.map((template) => (
              <MenuItem key={template.name} value={template.name}>
                <Box>
                  <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                    {template.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {template.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Selected Template Info */}
        {selectedTemplate && (
          <Box
            mt={2}
            p={2}
            style={{
              // backgroundColor: '#343639ff',
              borderRadius: '4px',
              border: '1px solid #3e404bff'
            }}
          >
            <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
              📋 Selected Template Details
            </Typography>
            <Typography variant="body2">
              <strong>Name:</strong> {selectedTemplate.name}
            </Typography>
            <Typography variant="body2">
              <strong>Description:</strong> {selectedTemplate.description}
            </Typography>
            <Typography variant="body2">
              <strong>Type:</strong> {selectedTemplate.type}
            </Typography>
            {/* <Typography variant="body2">
              <strong>Available Versions:</strong> {selectedTemplate.versions.join(', ')}
            </Typography> */}
            <Typography variant="body2">
              <strong>Current Version:</strong> {selectedTemplate.current_version}
            </Typography>
          </Box>
        )}
        {selectedTemplate?.name === 'empty-repo' && (
          <Box mt={2}>
            <Alert severity="warning">
              The <code>empty-repo</code> template is not intended for production-ready repositories. It does not include CI/CD pipeline configuration or standard repository structure.
              <br /><br />
              Please use it only for documentation or configuration-only repositories.
              <br /><br />
              📖 
              <a
                href={`${appUrl}/docs/default/component/backstage-docs/repo-templates/CI/Empty/`}
                target="_blank"
                rel="noopener noreferrer"
              >
                documentation
              </a>
            </Alert>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const templateSelectorCaseValidation = (
  value: TemplateSelectorValue,
  validation: FieldValidation,
) => {
  if (!value) {
    validation.addError('This field is required.');
    return;
  }
};

// Create the extension
console.log('📝 Creating TemplateSelector field extension...');

export const TemplateSelectorCaseFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'TemplateSelectorField',
    component: TemplateSelectorField,
    validation: templateSelectorCaseValidation,
  }),
);

console.log('✅ TemplateSelector field extension created with name: TemplateSelector');